import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../store/cartSlice";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import noimage from "./assets/images/noimage.webp";
import "./HomePage.css";

const HomePage = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Local State Pools
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [systemLoading, setSystemLoading] = useState(true);
  const [systemError, setSystemError] = useState(null);

  // Dynamic Home Content States (From Backend API)
  const [slides, setSlides] = useState([]);
  const [offerslides, setOfferslides] = useState([]);
  const [dealslides, setDealslides] = useState([]);
  const [instagramlinks, setInstagramlinks] = useState([]);
  const [faqData, setFaqData] = useState([]);

  const [openIndex, setOpenIndex] = useState(null);
  const answerRefs = useRef([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const autoSlideRef = useRef(null);

const [currentIndex, setCurrentIndex] = useState(0);



  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? dealslides.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === dealslides.length - 1 ? 0 : prevIndex + 1
    );
  };


  const toggleFAQ = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  // Slider controls
  const updateSlide = () => {
    const track = trackRef.current;
    if (!track) return;
    const slideElement = track.querySelector(".slide");
    if (slideElement) {
      const slideWidth = slideElement.getBoundingClientRect().width;
      track.style.transform = `translateX(-${idx * slideWidth}px)`;
    }
  };

  useEffect(() => {
    updateSlide();
  }, [idx]);

  useEffect(() => {
    window.addEventListener("resize", updateSlide);
    startAutoSlide();
    return () => {
      window.removeEventListener("resize", updateSlide);
      clearInterval(autoSlideRef.current);
    };
  }, [slides.length]);

  const nextSlide = () => {
    if (slides.length === 0) return;
    setIdx((prev) => (prev + 1) % slides.length);
    resetAllAnimations();
  };

  const prevSlide = () => {
    if (slides.length === 0) return;
    setIdx((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const startAutoSlide = () => {
    clearInterval(autoSlideRef.current);
    autoSlideRef.current = setInterval(() => nextSlide(), 5000);
  };

  const resetAutoSlide = () => {
    clearInterval(autoSlideRef.current);
    startAutoSlide();
  };

  const resetAllAnimations = () => {
    document.querySelectorAll("*").forEach((el) => {
      const style = getComputedStyle(el);
      if (style.animationName && style.animationName !== "none") {
        el.style.animation = "none";
        void el.offsetHeight;
        el.style.animation = "";
      }
    });
  };

  // Fetch Products, Categories, and Dynamic Homepage Layout Data
  useEffect(() => {
    const fetchStorefrontData = async () => {
      try {
        setSystemLoading(true);
        setSystemError(null);

        const [productsRes, categoriesRes, homeContentRes] = await Promise.all([
          fetch("https://rbuymart.rbhardwaj.com/server/api/products/get_all.php"),
          fetch("https://rbuymart.rbhardwaj.com/server/api/products/categories.php"),
          fetch("https://rbuymart.rbhardwaj.com/server/api/home/get_home_data.php")
        ]);

        if (!productsRes.ok || !categoriesRes.ok) {
          throw new Error("Failure fetching storefront metadata.");
        }

        const productsData = await productsRes.json();
        const categoriesData = await categoriesRes.json();

        setProducts(Array.isArray(productsData) ? productsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);

        if (homeContentRes.ok) {
          const homeContent = await homeContentRes.json();
          if (homeContent.status === "success" && homeContent.data) {
            setSlides(homeContent.data.slides || []);
            setOfferslides(homeContent.data.offerslides || []);
            setDealslides(homeContent.data.dealslides || []);
            setInstagramlinks(homeContent.data.instagramlinks || []);
            setFaqData(homeContent.data.faqData || []);
          }
        }
      } catch (err) {
        setSystemError(err.message);
      } finally {
        setSystemLoading(false);
      }
    };

    fetchStorefrontData();
  }, []);

  const handleAddToCartAction = (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!token) {
      window.dispatchEvent(
        new CustomEvent("TRIGGER_AUTH_MODAL", { detail: { mode: "login" } })
      );
      return;
    }

    let dynamicUserId = null;
    const userProfile = localStorage.getItem("user_profile");
    if (userProfile) {
      try {
        const parsed = JSON.parse(userProfile);
        dynamicUserId = parsed.id || parsed.user_id;
      } catch (err) {
        console.error("Failed to parse user profile context", err);
      }
    }

    dispatch(
      addToCart({
        user_id: dynamicUserId,
        product_id: product.id,
        variant_id: null,
        quantity: 1,
      })
    );

    alert("Your item has been added to the cart");
    navigate("/cart");
  };

  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter(
          (p) => p.category_id === parseInt(selectedCategory, 10)
        );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);

    try {
      const res = await fetch(
        "https://rbuymart.rbhardwaj.com/server/api/auth/sendemail.php",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await res.text();
      alert(data);
      setEmail("");
    } catch (err) {
      console.error("Error submitting form:", err);
    } finally {
      setLoading(false);
    }
  };

  if (systemLoading)
    return <div className="storefront-loader-splash">Loading...</div>;

  if (systemError)
    return (
      <div className="storefront-error-panel">Network issue: {systemError}</div>
    );

  return (
    <div className="homepage-root-layout">
  
 

      {/* 🟢 Hero Section */}
      <section className="hero-section">
        <div className="hero-categories">
          <div className="hero-categories-all">
            <i className="fa fa-bars"></i>
            <span> &#x2630; Shop by Categories</span>
          </div>
          <ul>
            {categories.map((cat) => (
              <li key={cat.id || cat.name}>
                <span style={{ fontSize: "25px" }}>&#x261E;</span>{" "}
                <Link to={`/shop?category=${cat.id}`}>{cat.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="hero-banner">
          <div className="Homepage slideshow" id="slideshow">
            <div className="track" id="track" ref={trackRef}>
              {slides.map((slide, i) => (
                <Link
                  key={i}
                  to={slide.link || "/shop"}
                  style={{ height: "100%", flex: "0 0 100%" }}
                >
                  <div
                    className="Maindiv slide"
                    style={{
                      background: slide.color || "radial-gradient(#72A003,#3B5400)",
                      backgroundImage: `url(${slide.img})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                    }}
                  ></div>
                </Link>
              ))}
            </div>

            {slides.length > 1 && (
              <div className="controls">
                <button
                  id="prev"
                  onClick={() => {
                    prevSlide();
                    resetAutoSlide();
                  }}
                >
                  ◀
                </button>
                <button
                  id="next"
                  onClick={() => {
                    nextSlide();
                    resetAutoSlide();
                  }}
                >
                  ▶
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 🟢 Feature Highlights */}
      <div className="counter_div section-div">
        <ul>
          <li>
            <div><i className="material-icons">&#xe558;</i></div>
            <span><h3>Free Shipping</h3>Free Shipping order over ₹9999</span>
          </li>
          <li>
            <div><i className="material-icons">&#xe8ba;</i></div>
            <span><h3>Easy Returns</h3>Flexible & Easy Returns Policy</span>
          </li>
          <li>
            <div><i className="material-icons">&#xe227;</i></div>
            <span><h3>Secure Payments</h3>100% Fast & Secure Payment</span>
          </li>
          <li>
            <div><i className="material-icons">&#xe311;</i></div>
            <span><h3>24/7 Support</h3>Expert Online Customer Support</span>
          </li>
        </ul>
      </div>

      {/* 🟢 Trending Products */}
      <section className="section-div">
        <div className="section-heading">
          <h2>Trending Products</h2>
        </div>

        <div className="horizontal-filter-div">
          <div className="horizontal-filter-scroll">
            <button
              className={`btn-filter-pill ${selectedCategory === "all" ? "active-pill" : ""}`}
              onClick={() => setSelectedCategory("all")}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`btn-filter-pill ${selectedCategory === String(cat.id) ? "active-pill" : ""}`}
                onClick={() => setSelectedCategory(String(cat.id))}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <Link to="/shop" className="btn-filter-pill">View all</Link>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="empty-catalog-fallback">Out of Stock</div>
        ) : (
          <div className="product-display-grid-structure">
            {filteredProducts.map((product) => (
              <Link key={product.id} to={`/product/${product.slug}`} className="product-card-anchor">
                <article className="product-item-card">
                   <sup class="price-parsent" style={{position:"absolute",left:"5px",top:'5px'}}>
                      <b>
                        {(
                    ((product.compare_at_price - product.price) /
                      product.compare_at_price) *
                    100
                  ).toFixed(0)}% 
                        </b>  
                        
                         <small> OFF</small></sup>
                  <div className="card-image-wrapper">
                    <img
                      src={
                        product.primary_image
                          ? `https://rbuymart.rbhardwaj.com/server/${product.primary_image}`
                          : "https://rbuymart.rbhardwaj.com/server/placeholder.webp"
                      }
                      alt={product.name}
                      loading="lazy"
                    />
                    {product.stock < 10 && product.stock > 0 && (
                      <span className="alert-badge-low-stock">Limited Allocation</span>
                    )}
                    {product.stock === 0 && (
                      <span className="alert-badge-out-of-stock">Depleted Stock</span>
                    )}
                  </div>

                  <div className="card-details-metadata-block">
                   
                    <div className="card-financial-footer-row">
                      <div className="price-matrix-stack">
                        <span className="current-market-price">
                          ₹{parseFloat(product.price || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                      <button
                        className="btn-add-to-cart-action-trigger"
                        disabled={product.stock === 0}
                        onClick={(e) => handleAddToCartAction(e, product)}
                      >
                         <i class="material-icons">&#xe8cc;</i>
                            {product.stock === 0 ? "" : ""}
                      </button>
                    </div>
                                 <div class="pdp-rating">
          <div class="pdp-stars">
            <span class="star filled">★</span><span class="star filled">★</span
            ><span class="star filled">★</span><span class="star filled">★</span
            ><span class="star half">★</span>
          </div>
          <span class="pdp-review-count">(8765)</span>
        </div>
                     <h3 className="product-title-heading">{product.name}</h3>
                    <p className="product-short-description-snippet">{product.short_description}</p>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* 🟢 Best Offers Section */}
      {offerslides.length > 0 && (
        <div className="adds-section section-div">
          <div className="section-heading">
            <h2>Best Offers</h2>
          </div>
          <Marquee pauseOnHover={true}>
            <div className="adds-div">
              {offerslides.map((slide, i) => (
                <Link key={i} to={slide.link || "/shop"}>
                  <div className="adds-img-box">
                    <img src={slide.img} alt={`offer-${i}`} />
                  </div>
                </Link>
              ))}
            </div>
          </Marquee>
        </div>
      )}

      {/* 🟢 Category Sections */}
      <div className="all-category-product-sections">
        {categories.map((cat) => {
          const categoryProducts = products.filter(
            (p) => String(p.category_id) === String(cat.id)
          );
          if (categoryProducts.length === 0) return null;

          return (
            <section key={cat.id} className="section-div category-block-section">
              <div className="section-heading-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="section-heading">
                  <h2>{cat.name}</h2>
                </div>
                <Link to={`/shop?category=${cat.id}`} className="btn-filter-pill" style={{ marginBottom: "20px" }}>
                  View All
                </Link>
              </div>

              <div className="product-display-grid-structure">
                {categoryProducts.map((product) => (
                  <Link key={product.id} to={`/product/${product.slug}`} className="product-card-anchor">
                    <article className="product-item-card">
                         <sup class="price-parsent" style={{position:"absolute",left:"5px",top:'5px'}}>
                      <b>
                        {(
                    ((product.compare_at_price - product.price) /
                      product.compare_at_price) *
                    100
                  ).toFixed(0)}% 
                        </b>  
                        
                         <small> OFF</small></sup>
                      <div className="card-image-wrapper">
                        <img
                          src={
                            product.primary_image
                              ? `https://rbuymart.rbhardwaj.com/server/${product.primary_image}`
                              : "https://rbuymart.rbhardwaj.com/server/placeholder.webp"
                          }
                          alt={product.name}
                          loading="lazy"
                        />
                      </div>
                      <div className="card-details-metadata-block">
                      
                        <div className="card-financial-footer-row">
                          <span className="current-market-price">
                            ₹{parseFloat(product.price || 0).toLocaleString("en-IN")}
                          </span>
                          <button
                            className="btn-add-to-cart-action-trigger"
                            disabled={product.stock === 0}
                            onClick={(e) => handleAddToCartAction(e, product)}
                          >
                              <i class="material-icons">&#xe8cc;</i>
                            {product.stock === 0 ? "" : ""}
                          </button>
                        </div>
                                     <div class="pdp-rating">
          <div class="pdp-stars">
            <span class="star filled">★</span><span class="star filled">★</span
            ><span class="star filled">★</span><span class="star filled">★</span
            ><span class="star half">★</span>
          </div>
          <span class="pdp-review-count">(8765)</span>
        </div>
                          <h3 className="product-title-heading">{product.name}</h3>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* 🟢 Top Deals Banner */}
      {dealslides.length > 0 && (
     <div className="deal-banner-section section-div">
      <div className="section-heading">
        <h2>Top Deals</h2>
      </div>

      <div className="slideshow-container">
        {dealslides.map((slide, index) => (
          <div
            key={slide.id || index}
            className={`mySlides fade ${index === currentIndex ? "active" : ""}`}
            style={{ display: index === currentIndex ? "block" : "none" }}
          >
            <Link to={slide.link || "/shop"}>
              <img src={slide.img} alt="best deal banner" style={{ width: "100%" }} />
            </Link>
          </div>
        ))}

        <button className="prev" onClick={handlePrev} aria-label="Previous Slide">
          ❮
        </button>
        <button className="next" onClick={handleNext} aria-label="Next Slide">
          ❯
        </button>
      </div>
    </div>
      )}

      {/* 🟢 Instagram Embed Section */}
      {instagramlinks.length > 0 && (
        <div className="instagram-section">
          {instagramlinks.map((id, i) => (
            <div
              key={i}
              className="instagram-card"
              style={{
                transform: `rotate(${i % 2 === 0 ? -10 : 10}deg)`,
                left: `${i * 200}px`,
              }}
            >
              <iframe
                src={`https://www.instagram.com/p/${id}/embed`}
                frameBorder="0"
                scrolling="no"
                allowTransparency="true"
                title={`insta-${i}`}
              ></iframe>
            </div>
          ))}
        </div>
      )}

      {/* 🟢 FAQ Section */}
      {faqData.length > 0 && (
        <div className="faq-section section-div">
          <div className="section-heading">
            <h2>Frequently Asked Questions</h2>
          </div>
          <br />
          {faqData.map((item, index) => (
            <div className="faq-item" key={index}>
              <div className="faq-question" onClick={() => toggleFAQ(index)}>
                <div style={{ display: "flex" }}>
                  <h4 style={{ width: "30px" }}>{index + 1}. </h4>
                  <span>{item.question}</span>
                </div>
                <span className={`icon ${openIndex === index ? "rotate" : ""}`}>+</span>
              </div>

              <div
                className={`faq-answer ${openIndex === index ? "open" : ""}`}
                ref={(el) => (answerRefs.current[index] = el)}
                style={{
                  maxHeight:
                    openIndex === index && answerRefs.current[index]
                      ? `${answerRefs.current[index].scrollHeight + 20}px`
                      : "0px",
                  overflow: "hidden",
                  transition: "all 0.4s ease",
                }}
              >
                <b>Ans. </b> {item.answer}
                <br /><br />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 🟢 Newsletter Subscription */}
      <div className="subscribe-section section-div">
        <div className="sec-title">
          <h2>Stay in the loop with our weekly newsletter</h2>
          <div className="title">
            Subscribe to get early notifications regarding exciting products and discounts.
          </div>
        </div>

        <div className="subscribe-form">
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Subscribe"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HomePage;